import { useEffect, useState } from 'react';
import { pdf } from '@react-pdf/renderer';

import { useFiles } from '@/entities/file';
import { INTERWILL_LANGUAGES } from '@/features/interwill';
import { LastWillPDF } from '@/shared/pdf-templates';
import { I18nPdfProvider } from '@/shared/providers/I18nPdfProvider';
import { files } from '@/shared/utils/files';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { FilePreviewData } from '@/widgets/modals/containers/FilePreviewModal/FilePreview';
import { useModalsStore } from '@/widgets/modals/modals.store';

import { useIwEditingStore, useIwExecutionStore } from '../stores';

export const useDisplayApprovedWill = (finalPdfUrl: string | null) => {
  const { i18n } = useTranslation('widgets/pdfs', { keyPrefix: 'lastWill' });

  const [decryptedPdf, setDecryptedPdf] = useState<File>();

  const { decryptDocumentFile } = useFiles();

  const modals = useModalsStore();
  const { testator } = useIwEditingStore();
  const { willNumber } = useIwExecutionStore();

  const getFileName = (lang: string) => {
    if (lang === 'EN') {
      return `InterWill ${testator.lastName} ${testator.firstName ?? ''} ${willNumber} (eng).pdf`;
    }
    return `Draft_InterWill ${testator.lastName} ${testator.firstName ?? ''} (${lang}).pdf`;
  };

  const onLanguageChange = async (lang = 'EN'): Promise<FilePreviewData> => {
    if (lang === 'EN' && decryptedPdf) {
      return {
        mode: 'fileInfo',
        fileInfo: {
          url: files.blobToUrl(decryptedPdf),
          name: getFileName(lang),
          fileType: 'pdf',
        },
      };
    }

    const i18nCloned = i18n.cloneInstance();
    await i18nCloned.changeLanguage(lang.toLowerCase());

    const doc = pdf(
      <I18nPdfProvider i18n={i18nCloned}>
        <LastWillPDF isDraft />
      </I18nPdfProvider>,
    );
    const blob = await doc.toBlob();

    return {
      mode: 'fileInfo',
      fileInfo: {
        url: files.blobToUrl(blob),
        fileType: 'pdf',
        name: getFileName(lang),
      },
    };
  };

  const openEnglish = (file: File) => {
    modals.openModal({
      type: 'FilePreviewModal',
      mode: 'fileInfo',
      fileInfo: {
        url: files.blobToUrl(file),
        fileType: 'pdf',
        name: getFileName('EN'),
      },
      supportedLanguages: INTERWILL_LANGUAGES,
      initLanguage: 'EN',
      onLanguageChange,
    });
  };

  const displayPdf = async () => {
    if (!decryptedPdf) {
      decryptFile(true);
      return;
    }
    openEnglish(decryptedPdf);
  };

  const decryptFile = async (openFile?: boolean) => {
    if (!finalPdfUrl) {
      return;
    }

    const file = await decryptDocumentFile(finalPdfUrl);

    if (!file) {
      return;
    }

    if (openFile) {
      openEnglish(file);
    }

    setDecryptedPdf(file);
  };

  useEffect(() => {
    decryptFile();
  }, []);

  return {
    displayPdf,
  };
};
